import React from 'react';
import { ScrollView, View, TouchableOpacity, Dimensions, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text, Card, ProgressBar } from '@ui';
import { useJourneys } from '@api/hooks';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '@navigation/types';

const { width } = Dimensions.get('window');
const CARD_WIDTH = (width - 48) / 2; // 2 columns with padding

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
`;

const Header = styled.View`
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const Grid = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing.sm}px;
  gap: ${({ theme }) => theme.spacing.base}px;
`;

const JourneyCard = styled(TouchableOpacity)<{ isActive: boolean }>`
  width: ${CARD_WIDTH}px;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md}px;
  padding: ${({ theme }) => theme.spacing.base}px;
  border-width: ${({ isActive }) => (isActive ? 2 : 1)}px;
  border-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.brandPrimary : theme.colors.border};
  ${Platform.OS === 'web'
    ? `
      box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
    `
    : `
      shadow-color: #000;
      shadow-offset: 0px 1px;
      shadow-opacity: 0.1;
      shadow-radius: 2px;
      elevation: 2;
    `}
`;

const IconCircle = styled.View<{ bgColor: string }>`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background-color: ${({ bgColor }) => bgColor};
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const ProgressContainer = styled.View`
  margin-top: ${({ theme }) => theme.spacing.sm}px;
`;

const ActiveBadge = styled.View`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.radius.full}px;
  padding: ${({ theme }) => theme.spacing.xs}px ${({ theme }) => theme.spacing.sm}px;
`;

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const JourneysScreen: React.FC = () => {
  const theme = useTheme();
  const navigation = useNavigation<NavigationProp>();
  const { data: journeys } = useJourneys();

  const handleJourneyPress = (journeyId: string) => {
    navigation.navigate('JourneyDetail', { journeyId });
  };

  return (
    <Container edges={['top']}>
      <Content showsVerticalScrollIndicator={false}>
        <Header>
          <Text variant="headline" color="textPrimary">
            All Journeys
          </Text>
          <Text variant="body" color="textSecondary" style={{ marginTop: 4 }}>
            Select a life moment to explore or start a new journey
          </Text>
        </Header>

        <Grid>
          {journeys?.map(journey => (
            <JourneyCard
              key={journey.id}
              isActive={journey.isActive}
              onPress={() => handleJourneyPress(journey.id)}
              activeOpacity={0.7}
            >
              {journey.isActive && (
                <ActiveBadge>
                  <Text variant="caption" color="textOnPrimary" style={{ fontSize: 10 }}>
                    Active
                  </Text>
                </ActiveBadge>
              )}
              <IconCircle bgColor={journey.color}>
                <Icon name={journey.icon} size={28} color={theme.colors.textOnPrimary} />
              </IconCircle>
              <Text variant="titleMedium" color="textPrimary" numberOfLines={2}>
                {journey.title}
              </Text>
              <Text
                variant="bodySmall"
                color="textSecondary"
                style={{ marginTop: 4 }}
                numberOfLines={2}
              >
                {journey.description}
              </Text>
              {journey.isActive && journey.progress > 0 && (
                <ProgressContainer>
                  <Text variant="caption" color="textSecondary">
                    {journey.progress}% complete
                  </Text>
                  <View style={{ marginTop: 4 }}>
                    <ProgressBar
                      progress={journey.progress}
                      height={4}
                      animated
                      color={theme.colors.brandPrimary}
                    />
                  </View>
                </ProgressContainer>
              )}
            </JourneyCard>
          ))}
        </Grid>

        <View style={{ height: 24 }} />
      </Content>
    </Container>
  );
};

export default JourneysScreen;
